import type {
  AccommodationRule,
  AccommodationType,
  BoardBasisFilter,
  ExperienceFilter,
  IngredientType,
  AmenitiesFilter,
  TransportDirection,
  ConnectionType,
} from '@libs/trip-planner-interfaces';

import { trackGoogleAnalyticsEvent } from './trackEvent';
import { GoogleAnalyticsEvent } from './eventsTypes';

export const enum TripPlannerViews {
  TripOVerview = 'tripOverview',
  HotelSelection = 'hotelSelection',
  FlightSelection = 'flightSelection',
}

// Note1: keep alphabetical order
// Note2: function name should have the following structure: track + GoogleAnalyticsEvent[Key]

export function trackAccommodationCardEdited(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.AccommodationCardEdited);
}

export function trackAccommodationFiltersAccommodationType(
  accommodationType: AccommodationType,
  isAdded: boolean
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersAccommodationType,
    {
      accommodationType,
      isAdded,
    }
  );
}

export function trackAccommodationFiltersBoardBasisFilter(
  filter: BoardBasisFilter,
  isAdded: boolean
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersBoardBasisFilter,
    {
      filter,
      isAdded,
    }
  );
}

export function trackAccommodationFiltersRulesFilters(
  accommodationRule: AccommodationRule,
  isAdded: boolean
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersRulesFilters,
    {
      accommodationRule,
      isAdded,
    }
  );
}

export function trackAccommodationFiltersStarsRate(star: number): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersStarsRate,
    {
      star,
    }
  );
}

export function trackAccommodationFiltersAmenitiesFilters(
  filter: AmenitiesFilter,
  isAdded: boolean
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersAmenitiesFilters,
    {
      filter,
      isAdded,
    }
  );
}

export function trackAccommodationFiltersExperienceFilters(
  filter: ExperienceFilter,
  isAdded: boolean
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.AccommodationFiltersExperienceFilters,
    {
      filter,
      isAdded,
    }
  );
}

export function trackActivitiesFilter(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.ActivitiesFilter);
}

export function trackActivitiesBannerForDestination(): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.ActivitiesBannerForDestination
  );
}

export function trackDestinationChanged(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.DestinationChanged);
}

export function trackDestinationAdded(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.DestinationAdded);
}

export function trackDestinationRemoved(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.DestinationRemoved);
}

export function trackErrorAddingBasket(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.ErrorAddingBasket);
}

export function trackEditNoAvailableIngredient(
  ingredient: IngredientType
): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.EditNoAvailableIngredient, {
    ingredient,
  });
}

export function trackGoToCheckout(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.GoToCheckout);
}

export function trackMonthChanged(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.MonthChanged);
}

export function trackNightsChanged(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.NightsChanged);
}

export function trackPaxEndChanging(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.PaxEndChanging);
}

export function trackPaxStartChanging(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.PaxStartChanging);
}

export function trackReisegigantenDeserializeOldRecipe(): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.ReisegigantenDeserializeOldRecipe
  );
}

export function trackReisegigantenNoAvailability(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.ReisegigantenNoAvailability);
}

export function trackNoTransportSelected(): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.NoTransportSelected);
}

export function trackTransportSelected(type: ConnectionType): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.TransportSelected, { type });
}

export function trackBookableTransportPortChanged(
  airportType: TransportDirection
): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.BookableTransportPortChanged, {
    airport: airportType,
  });
}

export interface HotelDetailsRoomOptions {
  total_visible: number;
  mapped: number;
  with_images: number;
}

export interface HotelDetailsViewItemParams {
  hotelType: AccommodationType;
  hotelCode: string;
  polygonId?: number;
  sessionId: string;
  clickId: string;
  affiliateId?: string;
  isFlexible: boolean;
  checkin: string;
  checkout: string;
  numberOfRooms: number;
  adults: number;
  children: number;
  roomOptions?: HotelDetailsRoomOptions;
}

export function trackHotelDetailsViewItemForECommerce(
  params: HotelDetailsViewItemParams
): void {
  trackGoogleAnalyticsEvent(
    GoogleAnalyticsEvent.HotelDetailsViewItemForECommerce,
    {
      session_id: params.sessionId,
      click_id: params.clickId,
      ecommerce: {
        items: [
          {
            item_id: params.hotelCode,
            item_category: 'Accommodation',
            item_category2: params.hotelType,
            hotel: {
              polygon_id: params.polygonId,
            },
          },
        ],
      },
      affiliate: {
        source: params.affiliateId !== undefined ? 'meta' : 'direct',
        id: params.affiliateId,
      },
      stay_parameters: {
        is_flexible: params.isFlexible,
        checkin: params.checkin,
        checkout: params.checkout,
        rooms: params.numberOfRooms,
        adults: params.adults,
        children: params.children,
      },
      room_options: params.roomOptions,
    }
  );
}

export interface TripOverviewInitiatedMetadata {
  totalAccomodation: number;
  totalFlight: number;
  totalFerry: number;
  totalActivity: number;
}

export function trackTripOverviewInitiated(
  metadata: TripOverviewInitiatedMetadata
): void {
  trackGoogleAnalyticsEvent(GoogleAnalyticsEvent.TripOverviewInitiated, {
    ...metadata,
  });
}
